/**
 * Payload builder, raw-template editor and live preview for a site webhook
 * (ENG-2427 items 2–3). The builder writes an ordinary `message_body`
 * template from today's placeholders, so the backend and partners' existing
 * templates are unchanged. The preview renders it in the browser with the
 * consumer's rules (SiteAboutPageUtils/webhookPayload.ts).
 */
import React from "react";
import { Box, Checkbox, TextField, Tooltip } from "@mui/material";
import {
  Hint,
  Mono,
  Pill,
  SectionLabel,
  TS_COLORS,
  TsButton,
} from "../../Troubleshooting/TroubleshootingUi";
import {
  MONO,
  MULISH,
} from "../../WireGuardTunnels/VpnDebugger/vpnDebugConstants";
import {
  BUILDER_FIELDS,
  BuilderRow,
  DELIVERED_EVENT_TYPES,
  EVENT_TYPE_LABEL,
  WEBHOOK_FIELDS,
  WebhookEventValue,
  buildTemplate,
  parseBuilderRows,
  renderWebhookPayload,
  templateWarnings,
} from "./SiteAboutPageUtils/webhookPayload";

type Mode = "builder" | "advanced";

const eventPills = (events: WebhookEventValue[]) =>
  events.length === DELIVERED_EVENT_TYPES.length ? (
    <Pill tone="grey">All events</Pill>
  ) : (
    events.map((event) => (
      <Pill key={event} tone="info">
        {EVENT_TYPE_LABEL[event]}
      </Pill>
    ))
  );

const Builder = ({
  rows,
  onRowsChange,
}: {
  rows: BuilderRow[];
  onRowsChange: (rows: BuilderRow[]) => void;
}) => (
  <Box
    data-testid="webhook-payload-builder"
    sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
  >
    {BUILDER_FIELDS.map((field) => {
      const row = rows.find((candidate) => candidate.field === field.key);
      return (
        <Box
          key={field.key}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            py: 0.25,
          }}
        >
          <Checkbox
            size="small"
            checked={Boolean(row)}
            inputProps={{ "aria-label": field.label }}
            onChange={(event) => {
              const checked = event.target.checked;
              onRowsChange(
                checked
                  ? [...rows, { field: field.key, key: field.key }]
                  : rows.filter((candidate) => candidate.field !== field.key)
              );
            }}
          />
          <Box sx={{ flex: "1 1 220px", minWidth: 0 }}>
            <Box sx={{ ...MULISH, fontSize: 14, fontWeight: 700 }}>
              {field.label}{" "}
              <Box component="span" sx={{ fontWeight: 400 }}>
                <Mono>{`{${field.key}}`}</Mono>
              </Box>
            </Box>
            <Hint>{field.description}</Hint>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {eventPills(field.events)}
          </Box>
          {row && (
            <TextField
              size="small"
              variant="standard"
              label="JSON key"
              value={row.key}
              inputProps={{
                "aria-label": `JSON key for ${field.label}`,
                spellCheck: false,
              }}
              sx={{ width: 180 }}
              onChange={(event) => {
                const nextKey = event.target.value;
                onRowsChange(
                  rows.map((candidate) =>
                    candidate.field === field.key
                      ? { ...candidate, key: nextKey }
                      : candidate
                  )
                );
              }}
            />
          )}
        </Box>
      );
    })}
  </Box>
);

const Advanced = ({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (value: string) => void;
  error?: string;
}) => {
  const inputRef = React.useRef<HTMLTextAreaElement | null>(null);

  const insert = (placeholder: string) => {
    const input = inputRef.current;
    const start = input?.selectionStart ?? value.length;
    const end = input?.selectionEnd ?? value.length;
    onChange(value.slice(0, start) + placeholder + value.slice(end));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <TextField
        multiline
        minRows={8}
        maxRows={20}
        value={value}
        error={Boolean(error)}
        helperText={
          error ||
          'JSON sent to the receiver. Put a field in quotes, e.g. "camera": "{camera_name}". Write {{ and }} for literal braces.'
        }
        inputRef={inputRef}
        inputProps={{
          "aria-label": "Message Body",
          spellCheck: false,
          style: { ...MONO, fontSize: 13 },
        }}
        onChange={(event) => {
          const nextValue = event.target.value;
          onChange(nextValue);
        }}
      />
      <Box>
        <SectionLabel>Fields — click to insert</SectionLabel>
        <Box
          sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.75 }}
          data-testid="webhook-field-reference"
        >
          {WEBHOOK_FIELDS.map((field) => (
            <Tooltip
              key={field.key}
              title={`${field.label}: ${field.description} (${field.events
                .map((event) => EVENT_TYPE_LABEL[event])
                .join(", ")})`}
            >
              <span>
                <TsButton
                  size="sm"
                  variant="quiet"
                  onClick={() => insert(`{${field.key}}`)}
                  sx={{ ...MONO, fontWeight: 400 }}
                >
                  {`{${field.key}}`}
                </TsButton>
              </span>
            </Tooltip>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export const WebhookPayloadPreview = ({
  messageBody,
  eventTypes,
  targetUrl,
}: {
  messageBody: string;
  eventTypes: string[];
  targetUrl: string;
}) => {
  const chosen = DELIVERED_EVENT_TYPES.filter((event) =>
    eventTypes.includes(event)
  );
  const choices = chosen.length > 0 ? chosen : DELIVERED_EVENT_TYPES;
  const [eventType, setEventType] = React.useState<WebhookEventValue>(
    choices[0]
  );
  const shown = choices.includes(eventType) ? eventType : choices[0];
  const result = renderWebhookPayload({
    messageBody,
    eventType: shown,
    targetUrl,
  });

  return (
    <Box
      data-testid="webhook-payload-preview"
      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
      >
        <SectionLabel>Preview</SectionLabel>
        {choices.map((event) => (
          <TsButton
            key={event}
            size="sm"
            variant={event === shown ? "primary" : "quiet"}
            color={event === shown ? TS_COLORS.navy : undefined}
            aria-pressed={event === shown}
            onClick={() => setEventType(event)}
          >
            {EVENT_TYPE_LABEL[event]}
          </TsButton>
        ))}
      </Box>
      <Hint>
        A sample {EVENT_TYPE_LABEL[shown].toLowerCase()} event, rendered the way
        the receiver gets it. Custom fields set on a camera are merged on top.
      </Hint>
      {result.error ? (
        <Box
          sx={{
            ...MULISH,
            fontSize: 13,
            color: TS_COLORS.red,
            backgroundColor: TS_COLORS.redTint,
            p: 1.5,
            borderRadius: "4px",
          }}
        >
          {result.error}
        </Box>
      ) : (
        <Box
          component="pre"
          data-testid="webhook-payload-preview-body"
          sx={{
            ...MONO,
            fontSize: 12.5,
            m: 0,
            p: 1.5,
            backgroundColor: "#fff",
            border: `1px solid ${TS_COLORS.line}`,
            borderRadius: "4px",
            maxHeight: 420,
            overflow: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
          }}
        >
          {result.body === null
            ? "(no request body)"
            : JSON.stringify(result.body, null, 2)}
        </Box>
      )}
      {result.notes.map((note) => (
        <Hint key={note} sx={{ color: TS_COLORS.amber }}>
          {note}
        </Hint>
      ))}
    </Box>
  );
};

export const WebhookPayloadEditor = ({
  value,
  onChange,
  eventTypes,
  targetUrl,
  error,
}: {
  value: string;
  onChange: (value: string) => void;
  eventTypes: string[];
  targetUrl: string;
  error?: string;
}) => {
  const [mode, setMode] = React.useState<Mode>(() =>
    parseBuilderRows(value) === null ? "advanced" : "builder"
  );
  const [rows, setRows] = React.useState<BuilderRow[]>(
    () => parseBuilderRows(value) ?? []
  );
  const builderRowsFromText = parseBuilderRows(value);
  const warnings = templateWarnings(value, eventTypes);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr)" },
        gap: 3,
      }}
    >
      <Box
        sx={{ display: "flex", flexDirection: "column", gap: 1.5, minWidth: 0 }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <TsButton
            size="sm"
            variant={mode === "builder" ? "primary" : "quiet"}
            color={mode === "builder" ? TS_COLORS.navy : undefined}
            aria-pressed={mode === "builder"}
            disabled={mode !== "builder" && builderRowsFromText === null}
            onClick={() => {
              setRows(builderRowsFromText ?? []);
              setMode("builder");
            }}
          >
            Pick fields
          </TsButton>
          <TsButton
            size="sm"
            variant={mode === "advanced" ? "primary" : "quiet"}
            color={mode === "advanced" ? TS_COLORS.navy : undefined}
            aria-pressed={mode === "advanced"}
            onClick={() => setMode("advanced")}
          >
            Advanced: edit JSON
          </TsButton>
          {mode === "advanced" && builderRowsFromText === null && (
            <Hint>
              This template has nesting or fixed text, so it can only be edited
              as JSON.
            </Hint>
          )}
        </Box>
        {mode === "builder" ? (
          <>
            <Hint>
              Tick the fields to send. Each becomes a key in a flat JSON object;
              rename the key to match what your receiver expects.
            </Hint>
            <Builder
              rows={rows}
              onRowsChange={(nextRows) => {
                setRows(nextRows);
                onChange(nextRows.length > 0 ? buildTemplate(nextRows) : "{}");
              }}
            />
          </>
        ) : (
          <Advanced value={value} onChange={onChange} error={error} />
        )}
        {warnings.length > 0 && (
          <Box
            data-testid="webhook-template-warnings"
            sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
          >
            {warnings.map((warning) => (
              <Hint key={warning.placeholder} sx={{ color: TS_COLORS.amber }}>
                {warning.message}
              </Hint>
            ))}
          </Box>
        )}
      </Box>
      <WebhookPayloadPreview
        messageBody={value}
        eventTypes={eventTypes}
        targetUrl={targetUrl}
      />
    </Box>
  );
};
